'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';
import { fadeInUp, staggerContainer, staggerItem, hoverLift } from '@/lib/animations';
import type { EventsModel } from '@/lib/mappers/eventsMapper';

interface PopularEventsSectionServerProps {
  initialData: EventsModel;
  dict: any;
}

const PopularEventsSectionServer = ({ initialData, dict }: PopularEventsSectionServerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  // Responsive items per view
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 1; // Mobile
      if (window.innerWidth < 768) return 2; // Small tablet
      if (window.innerWidth < 1024) return 3; // Tablet
      if (window.innerWidth < 1280) return 3; // Desktop
      return 3; // Large desktop
    }
    return 3;
  };

  // Update items per view on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    if (typeof window !== 'undefined') {
      setItemsPerView(getItemsPerView());
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, initialData.events.length - itemsPerView);
      const next = prev + 1;
      return next > maxIndex ? 0 : next;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, initialData.events.length - itemsPerView);
      const prevIndex = prev - 1;
      return prevIndex < 0 ? maxIndex : prevIndex;
    });
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'arrow-right':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        );
      case 'location':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        );
      case 'calendar':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        );
      case 'building':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        );
      default:
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        );
    }
  };

  return (
    <motion.section 
      className="pt-10 pb-16 bg-gradient-to-br from-gray-50 to-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.p
            className="font-semibold uppercase tracking-wide mb-3 text-[#4670BF]"
            style={{ fontSize: '15px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {initialData.badge}
          </motion.p>
          <motion.h2
            className="text-4xl md:text-5xl font-black text-gray-900 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            {initialData.title}
          </motion.h2>
        </div>

        {/* Events Carousel */}
        <motion.div 
          className="relative"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {/* Navigation Arrows */}
          {initialData.navigation.showArrows && initialData.events.length > itemsPerView && (
            <>
              <motion.button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-[#4670BF]"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                variants={staggerItem}
              >
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              
              <motion.button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-2xl p-4 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-[#4670BF]"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                variants={staggerItem}
              >
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
            </>
          )}

          {/* Events Carousel */}
          <div className="overflow-hidden pb-4">
            <motion.div 
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)` }}
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              {initialData.events.map((event, index) => (
                <div 
                  key={event.id} 
                  className="flex-shrink-0 px-3"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <motion.div 
                    className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col h-full hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-[#4670BF]/30"
                    variants={staggerItem}
                    whileHover={{ 
                      y: -8,
                      scale: 1.02
                    }}
                    transition={{ duration: 0.3 }}
                  >
                {/* Event Image */}
                <div className="aspect-video relative overflow-hidden">
                  {event.image.src ? (
                    <img
                      src={event.image.src}
                      alt={event.image.alt}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <motion.div 
                    className={`absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center ${event.image.src ? 'hidden' : 'flex'}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="text-center text-gray-600">
                      <svg className="w-20 h-20 mx-auto mb-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm font-medium">{event.image.placeholder}</p>
                    </div>
                  </motion.div>
                  {/* Corner badge for date */}
                  {event.date && (
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1 shadow">
                      <span className="text-xs font-bold text-gray-800">{event.date}</span>
                    </div>
                  )}
                </div>

                {/* Event Content */}
                <motion.div 
                  className="p-6 flex flex-col flex-grow"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                    {event.title}
                  </h3>
                  
                  <p className="text-lg text-gray-600 mb-4 line-clamp-3 flex-grow leading-relaxed">
                    {event.description}
                  </p>

                  {/* Event Details */}
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center text-base text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {getIcon('location')}
                      </svg>
                      {event.location}
                    </div>
                    <div className="flex items-center text-base text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {getIcon('calendar')}
                      </svg>
                      {event.date}
                    </div>
                    <div className="flex items-center text-base text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {getIcon('building')}
                      </svg>
                      {event.organization}
                    </div>
                  </div>

                  {/* Learn More Button - Always at bottom */}
                  <div className="mt-auto">
                    <Button
                      variant={(initialData.cta.variant as any) || 'primary'}
                      size={(initialData.cta.size as any) || 'lg'}
                      fullWidth
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {getIcon(initialData.cta.icon)}
                        </svg>
                      }
                    >
                      {initialData.cta.text}
                    </Button>
                  </div>
                </motion.div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default PopularEventsSectionServer;
